/*
 * CAPTURE-OWNED MODULE
 * Workflows list page for capture-ui
 * CRUD for workflow automations with card layout.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureAPI } from '../services/api';
import { useCanBuildWorkflows } from '../identity/useIsAdmin';
import './WorkflowsPage.css';

// Trigger type display metadata
const TRIGGER_TYPES = {
  manual_upload: { label: 'Manual Upload', icon: '⬆️' },
  cloud_source:  { label: 'Cloud Source',   icon: '☁️' },
  upload:        { label: 'On Upload',      icon: '📄' },
  email:         { label: 'Email Import',   icon: '📧' },
};

function formatDate(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString();
}

const WorkflowsPage = () => {
  const navigate = useNavigate();
  // 2026-06-14 power_user unit: create/edit/publish/delete are authoring
  // affordances for builders
  // (admin || power_user). The /workflows route is already RequireBuilder-guarded,
  // so this is defense-in-depth (true for anyone who reaches this page).
  const canBuild = useCanBuildWorkflows() !== false; // undefined (loading) treated as allowed here; route guard is the real gate
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState(1); // 1 = template pick, 2 = name/create
  const [selectedTemplate, setSelectedTemplate] = useState(null); // null = blank
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await captureAPI.workflows.list();
      if (res.ok) {
        setWorkflows(res.workflows || []);
      } else {
        throw new Error(res.error || 'Failed to load workflows');
      }
    } catch (err) {
      setError(err.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  const openCreateModal = async () => {
    setCreateStep(1);
    setSelectedTemplate(null);
    setCreateName('');
    setCreateDesc('');
    setCreateError(null);
    setShowCreateModal(true);
    // Fetch templates
    setTemplatesLoading(true);
    try {
      const res = await captureAPI.workflows.getTemplates();
      if (res.ok) setTemplates(res.templates || []);
    } catch (err) {
      console.error('[WorkflowsPage] Failed to load templates:', err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateStep(1);
    setSelectedTemplate(null);
    setCreateName('');
    setCreateDesc('');
    setCreateError(null);
  };

  const handleSelectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setCreateName(tpl ? tpl.name : '');
    setCreateDesc(tpl ? tpl.description : '');
    setCreateStep(2);
  };

  const handleCreate = async () => {
    if (!createName.trim()) {
      setCreateError('Name is required');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const payload = {
        name: createName.trim(),
        description: createDesc.trim() || null,
      };
      if (selectedTemplate) {
        payload.definition = selectedTemplate.definition;
      }
      const res = await captureAPI.workflows.create(payload);
      if (!res.ok) throw new Error(res.error || 'Failed to create workflow');
      closeCreateModal();
      navigate(`/workflows/${res.workflow.id}/designer`);
    } catch (err) {
      setCreateError(err.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  // The copy is a draft with its trigger bindings stripped server-side, so it
  // opens straight in the designer: a cloud-source or folder-watch copy is
  // unbound and needs a source picked before it can do anything.
  const handleDuplicate = async (id) => {
    try {
      const res = await captureAPI.workflows.duplicate(id);
      if (!res.ok) throw new Error(res.error);
      navigate(`/workflows/${res.workflow.id}/designer`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePublish = async (id) => {
    try {
      const res = await captureAPI.workflows.publish(id);
      if (!res.ok) throw new Error(res.error);
      setSuccessMsg('Workflow published');
      loadWorkflows();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUnpublish = async (id) => {
    try {
      const res = await captureAPI.workflows.unpublish(id);
      if (!res.ok) throw new Error(res.error);
      setSuccessMsg('Workflow unpublished');
      loadWorkflows();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Deactivate workflow "${name}"? It can be re-enabled later.`)) return;
    try {
      const res = await captureAPI.workflows.delete(id);
      if (!res.ok) throw new Error(res.error);
      setSuccessMsg('Workflow deactivated');
      loadWorkflows();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="wf-page">
      <div className="page-header">
        <h1>Workflows</h1>
      </div>

      <div className="actions-bar">
        {canBuild && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            + New Workflow
          </button>
        )}
      </div>

      {error && (
        <div className="inline-alert inline-alert-error" style={{ margin: '0 24px' }}>
          <span>{error}</span>
          <button className="inline-alert-dismiss" onClick={() => setError(null)}>×</button>
        </div>
      )}
      {successMsg && (
        <div className="inline-alert inline-alert-success" style={{ margin: '0 24px' }}>
          <span>{successMsg}</span>
          <button className="inline-alert-dismiss" onClick={() => setSuccessMsg(null)}>×</button>
        </div>
      )}

      <div className="wf-section">
        {loading ? (
          <div className="loading-container">
            <div className="spinner" />
            <p>Loading workflows...</p>
          </div>
        ) : workflows.length === 0 ? (
          <div className="wf-empty-state">
            <div className="wf-empty-state-icon">⚡</div>
            <h3>No Workflows Yet</h3>
            <p>Create your first workflow to automate document processing.</p>
            {canBuild && <button className="btn btn-primary" onClick={openCreateModal}>+ New Workflow</button>}
          </div>
        ) : (
          <div className="wf-grid">
            {workflows.map(wf => {
              const trigger = TRIGGER_TYPES[wf.trigger_type] || { label: wf.trigger_type || 'Manual', icon: '⚡' };
              return (
                <div key={wf.id} className={`wf-card ${!wf.is_active ? 'wf-card-inactive' : ''}`}>
                  <div className="wf-card-header">
                    <div className="wf-card-icon">⚡</div>
                    <div className="wf-card-title">
                      <h3 title={wf.name}>{wf.name}</h3>
                      <span className="wf-version">v{wf.version || 1}</span>
                      <div className="wf-card-badges">
                        <span className={`wf-badge ${wf.is_published ? 'wf-badge-published' : 'wf-badge-draft'}`}>
                          {wf.is_published ? 'Published' : 'Draft'}
                        </span>
                        <span className={`wf-badge ${wf.is_active ? 'wf-badge-active' : 'wf-badge-inactive'}`}>
                          {wf.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {wf.description && (
                    <p className="wf-card-desc">{wf.description}</p>
                  )}

                  <div className="wf-card-meta">
                    <div className="wf-meta-item">
                      <span className="wf-meta-icon">{trigger.icon}</span>
                      <span className="wf-meta-value">{trigger.label}</span>
                    </div>
                    <div className="wf-meta-item">
                      <span className="wf-meta-label">Runs:</span>
                      <span className="wf-meta-value">{wf.total_executions || 0}</span>
                    </div>
                    <div className="wf-meta-item">
                      <span className="wf-meta-label">Last run:</span>
                      <span className="wf-meta-value">{formatDate(wf.last_executed_at)}</span>
                    </div>
                  </div>

                  <div className="wf-card-actions">
                    {canBuild && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => navigate(`/workflows/${wf.id}/designer`)}
                      >
                        Edit
                      </button>
                    )}
                    {canBuild && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDuplicate(wf.id)}
                      >
                        Duplicate
                      </button>
                    )}
                    {/* R-publish REVERSED (2026-06-14): publish, unpublish and
                        delete are builder actions (admin||power_user), gated by
                        canBuildWorkflows — server enforces via accessPolicy
                        buildOnly. The whole /workflows route is builder-guarded. */}
                    {canBuild && (wf.is_published ? (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleUnpublish(wf.id)}
                      >
                        Unpublish
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handlePublish(wf.id)}
                      >
                        Publish
                      </button>
                    ))}
                    {canBuild && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(wf.id, wf.name)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Workflow Modal — two-step: template pick → name & create */}
      {showCreateModal && (
        <div className="wf-modal-overlay" onClick={closeCreateModal}>
          <div className={`wf-modal ${createStep === 1 ? 'wf-modal-wide' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="wf-modal-header">
              <h2>{createStep === 1 ? 'Choose a Template' : 'Name Your Workflow'}</h2>
              <button className="wf-modal-close" onClick={closeCreateModal}>×</button>
            </div>

            <div className="wf-modal-body">
              {createError && (
                <div className="inline-alert inline-alert-error" style={{ margin: '0 0 16px 0', padding: '10px 14px' }}>
                  <span>{createError}</span>
                </div>
              )}

              {/* ── Step 1: Template selection ── */}
              {createStep === 1 && (
                <>
                  {templatesLoading ? (
                    <div className="wf-tpl-loading">Loading templates...</div>
                  ) : (
                    <div className="wf-tpl-grid">
                      {/* Blank workflow card */}
                      <div className="wf-tpl-card wf-tpl-card-blank" onClick={() => handleSelectTemplate(null)}>
                        <div className="wf-tpl-card-icon">+</div>
                        <div className="wf-tpl-card-info">
                          <div className="wf-tpl-card-name">Blank Workflow</div>
                          <div className="wf-tpl-card-desc">Start from scratch</div>
                        </div>
                      </div>

                      {/* Template cards */}
                      {templates.map(tpl => (
                        <div
                          key={tpl.id}
                          className="wf-tpl-card"
                          onClick={() => handleSelectTemplate(tpl)}
                        >
                          <div className="wf-tpl-card-icon">{tpl.icon || '⚡'}</div>
                          <div className="wf-tpl-card-info">
                            <div className="wf-tpl-card-name">{tpl.name}</div>
                            <div className="wf-tpl-card-desc">{tpl.description}</div>
                            <span className="wf-tpl-card-category">{tpl.category?.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ── Step 2: Name and create ── */}
              {createStep === 2 && (
                <>
                  {selectedTemplate && (
                    <div className="wf-tpl-selected-banner">
                      <span className="wf-tpl-selected-icon">{selectedTemplate.icon || '⚡'}</span>
                      <span className="wf-tpl-selected-label">Template: {selectedTemplate.name}</span>
                      <button className="wf-tpl-change-btn" onClick={() => setCreateStep(1)}>Change</button>
                    </div>
                  )}
                  {!selectedTemplate && (
                    <div className="wf-tpl-selected-banner wf-tpl-selected-blank">
                      <span className="wf-tpl-selected-icon">+</span>
                      <span className="wf-tpl-selected-label">Blank Workflow</span>
                      <button className="wf-tpl-change-btn" onClick={() => setCreateStep(1)}>Change</button>
                    </div>
                  )}

                  <div className="wf-form-group">
                    <label>Workflow Name *</label>
                    <input
                      type="text"
                      className="wf-form-input"
                      value={createName}
                      onChange={e => setCreateName(e.target.value)}
                      placeholder="e.g. Invoice Processing Pipeline"
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter' && !creating) handleCreate(); }}
                    />
                  </div>

                  <div className="wf-form-group">
                    <label>Description</label>
                    <textarea
                      className="wf-form-input"
                      value={createDesc}
                      onChange={e => setCreateDesc(e.target.value)}
                      placeholder="Optional — describe what this workflow does"
                      rows={3}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="wf-modal-footer">
              {createStep === 2 && (
                <button className="btn btn-secondary" onClick={() => setCreateStep(1)} disabled={creating} style={{ marginRight: 'auto' }}>
                  ← Back
                </button>
              )}
              <button className="btn btn-secondary" onClick={closeCreateModal} disabled={creating}>Cancel</button>
              {createStep === 2 && (
                <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
                  {creating ? 'Creating...' : 'Create & Open Designer'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowsPage;
